import { Page, View, Text } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { TxtSection } from '@utils/constants';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';

interface MultiTouchPointListProps {
  event: PublicEventDetailsResponse;
  multiTouchPointItems: TxtSection[];
  inverse?: boolean;
}

const MultiTouchPointList = ({
  event,
  multiTouchPointItems,
  inverse = false,
}: MultiTouchPointListProps) => {
  const { t } = useTranslation();

  return (
    <View style={styles.multiTouchPointWrapper}>
      {multiTouchPointItems?.map((item, index) => {
        const isEven = inverse ? index % 2 !== 0 : index % 2 === 0;

        return (
          <View
            style={{
              ...styles.multiTouchPointItem,
              flexDirection: isEven ? 'row' : 'row-reverse',
            }}
            wrap={false}
            key={item.title}
          >
            <View style={styles.textWrapper}>
              <Text
                style={{
                  ...styles.itemTitle,
                  color: event?.design?.themeColor ?? 'black',
                }}
              >
                {item.title}
              </Text>
              <Text style={styles.itemSubtitle}>{item.text}</Text>
            </View>
            <View style={styles.imageWrapper}>
              <SafeImage
                style={{
                  ...styles.image,
                  borderTopRightRadius: isEven ? '4px' : 0,
                  borderBottomRightRadius: isEven ? '4px' : 0,
                  borderTopLeftRadius: !isEven ? '4px' : 0,
                  borderBottomLeftRadius: !isEven ? '4px' : 0,
                }}
                src={item.img}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
};

export interface MultitouchpointPageProps {
  event: PublicEventDetailsResponse;
  paragraphs: string[];
  multiTouchPointItems: TxtSection[];
  inverse?: boolean;
  footer: JSX.Element;
}

const MultitouchpointPage = ({
  event,
  multiTouchPointItems,
  paragraphs,
  footer,
  inverse = false,
}: MultitouchpointPageProps) => {
  const { t } = useTranslation();

  return (
    <Page
      size={{ width: 1440, height: 800 }}
      style={{ ...styles.page, overflow: 'hidden' }}
    >
      <View
        style={{
          ...styles.leftColumn,
          justifyContent: 'space-between',
          paddingBottom: 100,
        }}
      >
        <Text
          style={{
            ...styles.title,
            color: event?.design?.themeColor,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.2,
            textTransform: 'uppercase',
            marginBottom: 0,
          }}
        >
          {t('multi-touchpoint-networking-highlight')}
        </Text>

        <Text
          style={{
            ...styles.title,
            textTransform: 'uppercase',
          }}
        >
          {t('multi-touchpoint-networking')}
        </Text>

        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor ?? 'black',
            marginLeft: 0,
            marginTop: 0,
            marginBottom: 0,
            width: 120,
          }}
        />

        <View style={styles.paragraphWrapper}>
          {paragraphs?.map((paragraphKey: string) => {
            return (
              <Text key={paragraphKey} style={styles.paragraph}>
                {t(paragraphKey)}
              </Text>
            );
          })}
        </View>

        <MultiTouchPointList
          event={event}
          multiTouchPointItems={multiTouchPointItems?.slice(0, 2)}
          inverse={inverse}
        />
      </View>

      <View
        style={{
          ...styles.rightColumn,
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          paddingBottom: 100,
        }}
      >
        <MultiTouchPointList
          event={event}
          multiTouchPointItems={multiTouchPointItems?.slice(2, 5)}
          inverse={inverse}
        />
      </View>

      {footer}
    </Page>
  );
};

export default MultitouchpointPage;
